import Phaser from "phaser";
import { RectMask } from "@yjsgames/phaser-ui";
import {
  isOverflowing,
  marqueeOffsetBounds,
  clampDragOffset,
  advanceMarquee,
  computeScrollbarThumb,
  type MarqueeMetrics,
  type MarqueeState,
} from "./marqueeScroll.js";

/** 스크롤바 시각 옵션 — 지정 시 넘칠 때 텍스트 하단에 트랙+썸 표시. */
export interface MarqueeScrollbarConfig {
  /** 트랙/썸 두께(px). 기본 2. */
  readonly height?: number;
  /** 텍스트 하단으로부터 추가 간격(px). 기본 0. */
  readonly gap?: number;
  /** 썸 최소 폭(px). 기본 12. */
  readonly minThumbWidth?: number;
  /** 트랙 배경 색상(0xRRGGBB). */
  readonly trackColor: number;
  /** 썸(현재 위치 표시 막대) 색상(0xRRGGBB). */
  readonly thumbColor: number;
  /** 트랙 투명도(0~1). 기본 0.35. */
  readonly trackAlpha?: number;
  /** 썸 투명도(0~1). 기본 0.9. */
  readonly thumbAlpha?: number;
}

export interface MarqueeTextConfig {
  readonly x: number;
  readonly y: number;
  /** 클립 뷰포트 폭(px). */
  readonly width: number;
  readonly style: Phaser.Types.GameObjects.Text.TextStyle;
  /** Phaser depth(렌더 순서). 기본 0. */
  readonly depth?: number;
  /** 자동 흐름 속도(px/초). 기본 30. */
  readonly speedPxPerSec?: number;
  /** 경계 도달 후 멈춤(ms). 기본 800. */
  readonly holdMs?: number;
  /** 드래그 종료 후 자동 흐름 재개 지연(ms). 기본 1500. */
  readonly resumeDelayMs?: number;
  /** RTL이면 우측 정렬 + 흐름/드래그 방향 미러. */
  readonly rtl?: boolean;
  /** 지정 시 넘칠 때 가로 스크롤바 표시(트랙+썸). */
  readonly scrollbar?: MarqueeScrollbarConfig;
}

/** 마스크 높이 폴백 — text.height가 0(빈 텍스트/측정 전)일 때. */
const MASK_H_FALLBACK = 16;

/**
 * 가로 마키/드래그 스크롤 텍스트 (도메인 무관 범용 컴포넌트).
 *
 * 텍스트가 뷰폭을 넘치면 (1) 좌↔우 자동 마키 흐름 + (2) 사용자 드래그 수동 제어
 * (드래그 중 자동 정지, 종료 후 resumeDelayMs 뒤 재개). 넘치지 않으면 정지.
 * 마키 진행/드래그 클램프 계산은 `marqueeScroll.ts`(Phaser 무의존)에 분리해 단위테스트.
 *
 * 좌표: 내부 state.offset은 항상 LTR 논리 좌표([min≤0, 0]). RTL이면 시각 적용 시 부호를
 * 미러(visual=−offset, origin 우측)해 흡수 — 순수 모듈은 방향 무관.
 *
 * 마스크는 `RectMask`(world-space GeometryMask) 재사용. tick은 `scene.events("update")`에
 * self-subscribe하므로 외부 결선이 없다(destroy에서 off 필수).
 */
export class MarqueeText {
  private readonly text: Phaser.GameObjects.Text;
  private readonly zone: Phaser.GameObjects.Zone;
  private readonly mask: RectMask;
  /** 스크롤바(트랙·썸) — config.scrollbar 지정 시에만 생성. */
  private readonly scrollbar:
    | {
        readonly track: Phaser.GameObjects.Rectangle;
        readonly thumb: Phaser.GameObjects.Rectangle;
        readonly cfg: Required<Omit<MarqueeScrollbarConfig, "trackColor" | "thumbColor">> &
          Pick<MarqueeScrollbarConfig, "trackColor" | "thumbColor">;
      }
    | undefined;
  private readonly rtl: boolean;
  private readonly speedPxPerSec: number;
  private readonly holdMs: number;
  private readonly resumeDelayMs: number;

  private viewX: number;
  private viewY: number;
  private viewWidth: number;
  private baseX: number;
  private state: MarqueeState = { offset: 0, dir: -1, holdRemainMs: 0 };
  private visible = true;
  private dragging = false;
  private dragStartOffset = 0;
  private dragStartPointerX = 0;
  /** 드래그 종료 후 이 시각(ms)까지 자동 흐름 정지. */
  private resumeAt = 0;

  private readonly onUpdate: (time: number, delta: number) => void;

  constructor(
    private readonly scene: Phaser.Scene,
    cfg: MarqueeTextConfig,
  ) {
    this.rtl = cfg.rtl ?? false;
    this.speedPxPerSec = cfg.speedPxPerSec ?? 30;
    this.holdMs = cfg.holdMs ?? 800;
    this.resumeDelayMs = cfg.resumeDelayMs ?? 1500;
    this.viewX = cfg.x;
    this.viewY = cfg.y;
    this.viewWidth = cfg.width;
    this.baseX = this.computeBaseX();

    this.text = scene.add
      .text(this.baseX, cfg.y, "", cfg.style)
      .setOrigin(this.rtl ? 1 : 0, 0)
      .setDepth(cfg.depth ?? 0);
    this.mask = new RectMask(scene, this.maskRect());
    this.mask.apply(this.text);

    if (cfg.scrollbar) {
      const sb = cfg.scrollbar;
      const sbCfg = {
        height: sb.height ?? 2,
        gap: sb.gap ?? 0,
        minThumbWidth: sb.minThumbWidth ?? 12,
        trackAlpha: sb.trackAlpha ?? 0.35,
        thumbAlpha: sb.thumbAlpha ?? 0.9,
        trackColor: sb.trackColor,
        thumbColor: sb.thumbColor,
      };
      const depth = cfg.depth ?? 0;
      const track = scene.add
        .rectangle(0, 0, this.viewWidth, sbCfg.height, sbCfg.trackColor, sbCfg.trackAlpha)
        .setOrigin(0, 0)
        .setDepth(depth);
      const thumb = scene.add
        .rectangle(0, 0, sbCfg.minThumbWidth, sbCfg.height, sbCfg.thumbColor, sbCfg.thumbAlpha)
        .setOrigin(0, 0)
        .setDepth(depth);
      this.scrollbar = { track, thumb, cfg: sbCfg };
    }

    // 보이지 않는 드래그 핸들 — Container 직접 draggable 함정 회피(Zone은 scene 직속).
    this.zone = scene.add
      .zone(this.viewX + this.viewWidth / 2, this.viewY + MASK_H_FALLBACK / 2, this.viewWidth, MASK_H_FALLBACK)
      .setOrigin(0.5, 0.5);
    this.zone.on("dragstart", (pointer: Phaser.Input.Pointer) => {
      this.dragging = true;
      this.dragStartOffset = this.state.offset;
      this.dragStartPointerX = pointer.x;
    });
    this.zone.on("drag", (pointer: Phaser.Input.Pointer) => {
      // RTL이면 화면 드래그 방향과 논리 offset 방향이 반대 → deltaX 부호 미러.
      const screenDelta = pointer.x - this.dragStartPointerX;
      const deltaX = this.rtl ? -screenDelta : screenDelta;
      const offset = clampDragOffset(this.dragStartOffset, deltaX, this.metrics());
      this.state = { ...this.state, offset };
      this.applyOffset();
    });
    this.zone.on("dragend", () => {
      this.dragging = false;
      this.resumeAt = this.scene.time.now + this.resumeDelayMs;
    });

    this.onUpdate = (time, delta) => this.tick(time, delta);
    scene.events.on(Phaser.Scenes.Events.UPDATE, this.onUpdate);
  }

  /** 텍스트 갱신 — 넘침 재판정 + offset 리셋(처음부터 흐름, 시작 hold 부여). */
  setText(text: string): void {
    this.text.setText(text);
    this.state = { offset: 0, dir: -1, holdRemainMs: this.holdMs };
    this.refresh();
  }

  /** 뷰포트(위치/폭) 갱신 — 펼침/접힘 resize. 마스크·baseX 갱신 + offset 재클램프. */
  setViewport(x: number, y: number, width: number): void {
    this.viewX = x;
    this.viewY = y;
    this.viewWidth = width;
    this.baseX = this.computeBaseX();
    this.text.setY(y);
    this.refresh();
  }

  /** 가시성 전환 — 숨김 시 드래그 Zone도 비활성화해 다른 오브젝트의 입력 이벤트를 가로채지 않음. */
  setVisible(visible: boolean): void {
    this.visible = visible;
    this.text.setVisible(visible).setActive(visible);
    this.zone.setActive(visible);
    this.syncZone();
    this.updateScrollbar();
  }

  /** scene update 리스너 해제 + text·Zone·mask·scrollbar 게임 오브젝트 전부 파괴. */
  destroy(): void {
    this.scene.events.off(Phaser.Scenes.Events.UPDATE, this.onUpdate);
    this.zone.destroy();
    this.text.destroy();
    this.mask.destroy();
    if (this.scrollbar) {
      this.scrollbar.track.destroy();
      this.scrollbar.thumb.destroy();
    }
  }

  /** 마스크 rect·zone 위치·넘침 판정·offset 클램프를 현재 상태로 일괄 재계산. */
  private refresh(): void {
    this.mask.setRect(this.maskRect());
    const m = this.metrics();
    const { min, max } = marqueeOffsetBounds(m);
    const clamped = Math.min(max, Math.max(min, this.state.offset));
    this.state = { ...this.state, offset: clamped };
    this.syncZone();
    this.applyOffset();
  }

  private tick(time: number, delta: number): void {
    if (!this.visible) return;
    const m = this.metrics();
    if (!isOverflowing(m)) {
      if (this.state.offset !== 0) {
        this.state = { offset: 0, dir: -1, holdRemainMs: 0 };
        this.applyOffset();
      }
      return;
    }
    if (this.dragging || time < this.resumeAt) return;
    this.state = advanceMarquee(this.state, m, delta, {
      speedPxPerSec: this.speedPxPerSec,
      holdMs: this.holdMs,
    });
    this.applyOffset();
  }

  private applyOffset(): void {
    const visual = this.rtl ? -this.state.offset : this.state.offset;
    this.text.setX(this.baseX + visual);
    this.updateScrollbar();
  }

  /** 스크롤바 트랙/썸 위치·폭·가시성 갱신(넘침 + 가시 시에만 표시). */
  private updateScrollbar(): void {
    if (!this.scrollbar) return;
    const { track, thumb, cfg } = this.scrollbar;
    const show = this.visible && isOverflowing(this.metrics());
    track.setVisible(show).setActive(show);
    thumb.setVisible(show).setActive(show);
    if (!show) return;
    const h = this.text.height || MASK_H_FALLBACK;
    const y = this.viewY + h + cfg.gap;
    track.setPosition(this.viewX, y).setSize(this.viewWidth, cfg.height);
    const t = computeScrollbarThumb(
      this.metrics(),
      this.state.offset,
      this.viewX,
      this.rtl,
      cfg.minThumbWidth,
    );
    thumb.setPosition(t.x, y).setSize(t.width, cfg.height);
  }

  private metrics(): MarqueeMetrics {
    return { textWidth: this.text.width, viewWidth: this.viewWidth };
  }

  private computeBaseX(): number {
    return this.rtl ? this.viewX + this.viewWidth : this.viewX;
  }

  private maskRect(): { x: number; y: number; width: number; height: number } {
    return {
      x: this.viewX,
      y: this.viewY,
      width: this.viewWidth,
      height: this.text.height || MASK_H_FALLBACK,
    };
  }

  /** 넘침 + 가시 상태일 때만 드래그 핸들 활성(그리드 셀 드래그 가로채기 회피). */
  private syncZone(): void {
    const h = this.text.height || MASK_H_FALLBACK;
    this.zone.setPosition(this.viewX + this.viewWidth / 2, this.viewY + h / 2);
    this.zone.setSize(this.viewWidth, h);
    if (this.visible && isOverflowing(this.metrics())) {
      this.zone.setInteractive({ draggable: true });
    } else {
      this.zone.disableInteractive();
    }
  }
}
